import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConfirmModal from '@/components/common/ConfirmModal';
import ActionNotice, {
    useActionNotice,
} from '@/components/common/ActionNotice';
import { STATUS_LABELS } from '@/shared/constants/listings';
import listingService from '@/shared/services/listingService';
import { serverMessage } from '@/shared/utils/serverMessage';
import usePageTitle from '@/shared/hooks/usePageTitle';
import ListingCard from './components/ListingCard';
import CompleteExchangeModal from './components/CompleteExchangeModal';

// Browse defaults to the open statuses, so a member's own inventory has to ask
// for the rest by name. This page is the only place a removed listing is
// visible at all.
const ALL_STATUSES = Object.keys(STATUS_LABELS).join(',');

// The route pages at 24 by default and caps a page at 100. This page groups
// its cards by status rather than paging them, so it asks for the cap in one
// read: a member reading a group heading must not be shown part of a group as
// if it were the whole of it.
const PAGE_LIMIT = 100;

// A claimed item is still in flight: the owner owes the requester either a
// close-out or a released claim, so it stands apart from the group there is
// nothing left to do about.
const GROUPS = [
    { heading: 'Open', statuses: ['available', 'requested'] },
    { heading: 'Claimed', statuses: ['claimed'] },
    { heading: 'Expired', statuses: ['expired'] },
    { heading: 'Closed', statuses: ['completed', 'removed'] },
];

function MyListingsPage() {
    usePageTitle('My listings');
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const {
        ref: noticeRef,
        notice,
        error: actionError,
        report,
    } = useActionNotice();
    const [pendingRemoval, setPendingRemoval] = useState(null);
    const [pendingComplete, setPendingComplete] = useState(null);
    const [pendingRelease, setPendingRelease] = useState(null);

    const load = useCallback(async () => {
        const data = await listingService.list({
            mine: 'true',
            status: ALL_STATUSES,
            limit: PAGE_LIMIT,
        });
        setListings(data.listings || []);
    }, []);

    useEffect(() => {
        async function first() {
            try {
                await load();
            } catch (_error) {
                setError(
                    'Could not load your listings. Check your connection and' +
                        ' try again.'
                );
            } finally {
                setLoading(false);
            }
        }
        first();
    }, [load]);

    const replace = (updated) =>
        setListings((previous) =>
            previous.map((item) =>
                item._id === updated._id ? { ...item, ...updated } : item
            )
        );

    /**
     * Apply one transition and report what the server said about it.
     *
     * A refusal names what actually happened, which a retry line cannot: a
     * 409 on release means the claim is already gone. The list is reread
     * after a refusal as well, because the card that offered the action is
     * then showing a state that no longer exists.
     *
     * Every outcome goes to the notice region, success included. The button
     * that was clicked is often gone by the time the read returns, because
     * its card has moved to another group, so a member who did not see the
     * card move has nothing else telling them the action landed.
     */
    const runAction = async (act, done, fallback) => {
        try {
            replace(await act());
            report({ notice: done });
        } catch (caught) {
            report({ error: serverMessage(caught, fallback) });
            try {
                await load();
            } catch (_error) {
                // The list is unreadable now; the message above still stands.
            }
        }
    };

    const handleRelist = (item) =>
        runAction(
            () => listingService.relist(item._id),
            `${item.title} is back on offer for another 30 days.`,
            'Could not relist that item. Try again.'
        );

    const handleRemove = () => {
        const target = pendingRemoval;
        setPendingRemoval(null);
        return runAction(
            () => listingService.remove(target._id),
            `${target.title} no longer appears on browse.`,
            'Could not remove that item. Try again.'
        );
    };

    const handleComplete = (outcome) => {
        const target = pendingComplete;
        setPendingComplete(null);
        return runAction(
            () => listingService.complete(target._id, outcome),
            `${target.title} is closed out and moved to Closed.`,
            'Could not complete that exchange. Try again.'
        );
    };

    const handleRelease = () => {
        const target = pendingRelease;
        setPendingRelease(null);
        return runAction(
            () => listingService.release(target._id),
            `The claim on ${target.title} is released and it is back on` +
                ' offer.',
            'Could not release that claim. Try again.'
        );
    };

    if (loading) {
        return (
            <Container className="py-4">
                <LoadingSpinner message="Loading your listings" />
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <Row className="align-items-center mb-3">
                <Col>
                    <h1 className="h3 mb-0">My listings</h1>
                </Col>
                <Col xs="auto">
                    <Button as={Link} to="/listings/new">
                        Post an item
                    </Button>
                </Col>
            </Row>
            {error && <Alert variant="danger">{error}</Alert>}
            <ActionNotice ref={noticeRef} notice={notice} error={actionError} />
            {!error && listings.length === 0 && (
                <p className="text-muted">
                    You have not posted anything yet. Post an item to get
                    started.
                </p>
            )}
            {listings.length > 0 &&
                GROUPS.map((group) => {
                    const items = listings.filter((item) =>
                        group.statuses.includes(item.status)
                    );
                    return (
                        <section
                            key={group.heading}
                            aria-label={group.heading}
                            className="mb-4"
                        >
                            <h2 className="h5">{group.heading}</h2>
                            {items.length === 0 ? (
                                <p className="text-muted">
                                    Nothing in this group.
                                </p>
                            ) : (
                                <Row xs={1} md={2} lg={3} className="g-3">
                                    {items.map((item) => (
                                        <Col key={item._id}>
                                            <ListingCard
                                                listing={item}
                                                titleAs="h3"
                                                footer={
                                                    group.heading ===
                                                    'Closed' ? null : (
                                                        <>
                                                            {group.heading ===
                                                                'Open' && (
                                                                <>
                                                                    <Button
                                                                        as={
                                                                            Link
                                                                        }
                                                                        to={`/listings/${item._id}/edit`}
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        aria-label={`Edit ${item.title}`}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                    {/* Most handoffs are
                                                            arranged by mail
                                                            off the exchange,
                                                            so the close-out
                                                            an owner needs
                                                            most is on a
                                                            listing nobody
                                                            formally
                                                            requested. */}
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        aria-label={`Mark completed: ${item.title}`}
                                                                        onClick={() =>
                                                                            setPendingComplete(
                                                                                item
                                                                            )
                                                                        }
                                                                    >
                                                                        Mark
                                                                        completed
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        aria-label={`Remove ${item.title}`}
                                                                        onClick={() =>
                                                                            setPendingRemoval(
                                                                                item
                                                                            )
                                                                        }
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {group.heading ===
                                                                'Claimed' && (
                                                                <>
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        aria-label={`Mark completed: ${item.title}`}
                                                                        onClick={() =>
                                                                            setPendingComplete(
                                                                                item
                                                                            )
                                                                        }
                                                                    >
                                                                        Mark
                                                                        completed
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        aria-label={`Release claim: ${item.title}`}
                                                                        onClick={() =>
                                                                            setPendingRelease(
                                                                                item
                                                                            )
                                                                        }
                                                                    >
                                                                        Release
                                                                        claim
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {group.heading ===
                                                                'Expired' && (
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    aria-label={`Relist ${item.title}`}
                                                                    onClick={() =>
                                                                        handleRelist(
                                                                            item
                                                                        )
                                                                    }
                                                                >
                                                                    Relist
                                                                </Button>
                                                            )}
                                                        </>
                                                    )
                                                }
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </section>
                    );
                })}
            <ConfirmModal
                show={Boolean(pendingRemoval)}
                title="Remove this listing?"
                confirmLabel="Remove listing"
                cancelLabel="Keep it"
                onCancel={() => setPendingRemoval(null)}
                onConfirm={handleRemove}
            >
                <p className="mb-0">
                    {pendingRemoval?.title} stops appearing on browse. You keep
                    it in this list, but it cannot be relisted.
                </p>
            </ConfirmModal>
            <CompleteExchangeModal
                show={Boolean(pendingComplete)}
                listingTitle={pendingComplete?.title}
                claimed={pendingComplete?.status === 'claimed'}
                onCancel={() => setPendingComplete(null)}
                onConfirm={handleComplete}
            />
            <ConfirmModal
                show={Boolean(pendingRelease)}
                title="Release the claim?"
                confirmLabel="Release the claim"
                cancelLabel="Keep the claim"
                onCancel={() => setPendingRelease(null)}
                onConfirm={handleRelease}
            >
                <p className="mb-0">
                    {pendingRelease?.title} goes back on offer and the accepted
                    request is declined.
                </p>
            </ConfirmModal>
        </Container>
    );
}

export default MyListingsPage;
